const SAMPLE_WINDOW_MS = 900;
const MIN_SCALE = 0.5;
const MAX_SCALE = 1;

type RenderScaleSample = {
  fps: number;
  scale: number;
  scaleChanged: boolean;
};

/**
 * Preserves the native requestAnimationFrame cadence and adapts pixel density only.
 * No visual subsystem, shader effect, animation, or simulation step is disabled.
 */
export class AdaptiveRenderScaleController {
  private targetRefreshHz = 60;
  private scale = MAX_SCALE;
  private sampleStartedAt = 0;
  private frameCount = 0;
  private pressureWindows = 0;
  private recoveryWindows = 0;

  get renderScale() {
    return this.scale;
  }

  setTargetRefreshHz(refreshHz: number) {
    const safeRefreshHz = Number.isFinite(refreshHz) ? refreshHz : 60;
    this.targetRefreshHz = Math.min(Math.max(safeRefreshHz, 30), 360);
    this.resetSampling();
  }

  resetSampling() {
    this.sampleStartedAt = 0;
    this.frameCount = 0;
    this.pressureWindows = 0;
    this.recoveryWindows = 0;
  }

  sample(time: number): RenderScaleSample | null {
    if (!this.sampleStartedAt) {
      this.sampleStartedAt = time;
      return null;
    }

    this.frameCount += 1;
    const duration = time - this.sampleStartedAt;
    if (duration < SAMPLE_WINDOW_MS) return null;

    const fps = (this.frameCount * 1000) / duration;
    const ratio = fps / this.targetRefreshHz;
    const previousScale = this.scale;
    this.sampleStartedAt = time;
    this.frameCount = 0;

    if (ratio < 0.65) {
      this.scale = Math.max(MIN_SCALE, this.scale - 0.18);
      this.pressureWindows = 0;
      this.recoveryWindows = 0;
    } else if (ratio < 0.8) {
      this.scale = Math.max(MIN_SCALE, this.scale - 0.12);
      this.pressureWindows = 0;
      this.recoveryWindows = 0;
    } else if (ratio < 0.92) {
      this.scale = Math.max(MIN_SCALE, this.scale - 0.07);
      this.pressureWindows = 0;
      this.recoveryWindows = 0;
    } else if (ratio < 0.97 && this.scale > MIN_SCALE) {
      this.pressureWindows += 1;
      this.recoveryWindows = 0;
      if (this.pressureWindows >= 2) {
        this.scale = Math.max(MIN_SCALE, this.scale - 0.035);
        this.pressureWindows = 0;
      }
    } else {
      this.pressureWindows = 0;
      if (ratio >= 0.992 && this.scale < MAX_SCALE) this.recoveryWindows += 1;
      else this.recoveryWindows = 0;
      if (this.recoveryWindows >= 4) {
        this.scale = Math.min(MAX_SCALE, this.scale + 0.025);
        this.recoveryWindows = 0;
      }
    }

    return { fps, scale: this.scale, scaleChanged: this.scale !== previousScale };
  }
}
